import dayjs from 'dayjs';
import {
  buildMetricChartSeriesFromBuckets,
  buildStatusChartRows,
  type DailyExecutionMetricBucket,
} from '@/features/shared/utils/chartBucketSeries';
import type { MiniMetricChartSummary } from './MiniMetricChartHeader';
import type {
  MetricsStatusChartPanelDisplayModel,
  MetricsStatusChartPanelModelToolbarState,
} from './useMetricsStatusChartPanelModel';
import type { MiniMetricLineChartDatum } from '@/features/executions/utils/chartData';
import type { MetricValueItem } from '@/features/metrics/components/metricValueItems';
import type { DateQuickFilter } from '@/features/shared/hooks/useDateWindow';
import type { UiTone } from '@/theme/uiTokens';

const mapMetricValueTone = (
  tone: MetricValueItem['valueTone'],
): MiniMetricChartSummary['valueTone'] => {
  switch (tone) {
    case 'default':
    case 'neutral':
    case 'success':
    case 'green':
      return 'green';
    case 'error':
    case 'red':
      return 'red';
    case 'warning':
    case 'orange':
    case 'yellow':
      return 'orange';
    case 'brown':
      return 'brown';
    case 'info':
    case 'blue':
      return 'blue';
    case 'primary':
    case 'teal':
      return 'teal';
    case 'pink':
    case 'grey':
    case 'transparent':
      return 'neutral';
    case 'purple':
      return 'purple';
  }
};

export const toMiniMetricSummary = (
  item: MetricValueItem | null,
): MiniMetricChartSummary | null => {
  if (!item) return null;
  return {
    valueLabel: item.valueLabel,
    valueTone: mapMetricValueTone(item.valueTone),
    deltaLabel: item.deltaLabel ?? null,
    deltaValue: item.deltaValue,
    deltaTone: item.deltaTone,
    isMissing: item.valueLabel === '-',
  };
};

export const buildMetricsChartToolbarState = <TStatus extends string>({
  search,
  setSearch,
  selectedStatuses,
  setSelectedStatuses,
  selectedTags,
  setSelectedTags,
  noRowsText,
  dateQuickFilter,
  onDateQuickFilterChange,
  startDay,
  endDay,
  latestQueryStartDay,
  latestQueryEndDay,
  onFromDateChange,
  onToDateChange,
}: {
  search: string;
  setSearch: (value: string) => void;
  selectedStatuses: TStatus[];
  setSelectedStatuses: (value: TStatus[]) => void;
  selectedTags: string[];
  setSelectedTags: (value: string[]) => void;
  noRowsText: string;
  dateQuickFilter: DateQuickFilter;
  onDateQuickFilterChange: (value: DateQuickFilter) => void;
  startDay: string | null;
  endDay: string | null;
  latestQueryStartDay?: string | null;
  latestQueryEndDay?: string | null;
  onFromDateChange: (value: string | null) => void;
  onToDateChange: (value: string | null) => void;
}): MetricsStatusChartPanelModelToolbarState<TStatus> => ({
  search,
  setSearch,
  selectedStatuses,
  setSelectedStatuses,
  selectedTags,
  setSelectedTags,
  noRowsText,
  dateQuickFilter,
  onDateQuickFilterChange,
  latestQueryStartDay,
  latestQueryEndDay,
  fromDate: startDay ? dayjs(startDay) : null,
  toDate: endDay ? dayjs(endDay) : null,
  onFromDateChange,
  onToDateChange,
});

export const buildMetricsChartModelOverride = ({
  statusChartData,
  passRateChartData,
  frequencyChartData,
  durationChartData,
  flakeScoreChartData,
  passRateMiniChartData,
  frequencyMiniChartData,
  durationMiniChartData,
  flakeScoreMiniChartData,
  metricValueItems,
  facetCounts,
  thresholdScope = 'executionRun',
}: {
  thresholdScope?: MetricsStatusChartPanelDisplayModel['thresholdScope'];
  statusChartData: MetricsStatusChartPanelDisplayModel['statusChartData'];
  passRateChartData: MetricsStatusChartPanelDisplayModel['passRateChartData'];
  frequencyChartData: MetricsStatusChartPanelDisplayModel['frequencyChartData'];
  durationChartData: MetricsStatusChartPanelDisplayModel['durationChartData'];
  flakeScoreChartData?: MetricsStatusChartPanelDisplayModel['flakeScoreChartData'];
  passRateMiniChartData?: MetricsStatusChartPanelDisplayModel['passRateMiniChartData'];
  frequencyMiniChartData?: MetricsStatusChartPanelDisplayModel['frequencyMiniChartData'];
  durationMiniChartData?: MetricsStatusChartPanelDisplayModel['durationMiniChartData'];
  flakeScoreMiniChartData?: MetricsStatusChartPanelDisplayModel['flakeScoreMiniChartData'];
  metricValueItems: MetricValueItem[];
  facetCounts?: MetricsStatusChartPanelDisplayModel['facetCounts'];
}): MetricsStatusChartPanelDisplayModel => {
  const metricItemsById = Object.fromEntries(
    metricValueItems.map((item) => [item.id, item]),
  ) as Partial<Record<'passRate' | 'frequency' | 'avgDuration' | 'flakeScore', MetricValueItem>>;

  return {
    thresholdScope,
    statusChartData,
    passRateChartData,
    frequencyChartData,
    flakeScoreChartData: flakeScoreChartData ?? null,
    durationChartData,
    passRateMiniChartData:
      passRateMiniChartData ?? toMiniMetricLineChartData(passRateChartData, 'passRate'),
    frequencyMiniChartData:
      frequencyMiniChartData ?? toMiniMetricLineChartData(frequencyChartData, 'frequency'),
    durationMiniChartData:
      durationMiniChartData ?? toMiniMetricLineChartData(durationChartData, 'duration'),
    flakeScoreMiniChartData:
      flakeScoreMiniChartData ??
      toMiniMetricLineChartData(flakeScoreChartData ?? null, 'flakeScore'),
    showStatusChartSkeleton: false,
    showChartErrorState: false,
    passRateSummary: toMiniMetricSummary(metricItemsById.passRate ?? null),
    frequencySummary: toMiniMetricSummary(metricItemsById.frequency ?? null),
    flakeScoreSummary: toMiniMetricSummary(metricItemsById.flakeScore ?? null),
    durationSummary: toMiniMetricSummary(metricItemsById.avgDuration ?? null),
    facetCounts: facetCounts ?? {
      statusCounts: {},
      entityCounts: {},
    },
  };
};

const buildStatusFacetCountsFromBuckets = <TStatus extends string>(
  statusCountsByDate: ReadonlyMap<string, Record<TStatus, number>>,
): Partial<Record<string, number>> => {
  const counts: Partial<Record<string, number>> = {};

  statusCountsByDate.forEach((bucket) => {
    Object.entries(bucket).forEach(([status, count]) => {
      counts[status] = (counts[status] ?? 0) + Number(count);
    });
  });

  return counts;
};

const toMiniMetricLineChartData = (
  data: Array<Record<string, string | number | null>> | null,
  key: 'passRate' | 'frequency' | 'duration' | 'flakeScore',
): MiniMetricLineChartDatum[] | null =>
  data?.map((row) => {
    const rawValue = row[key];
    const measuredValue =
      typeof rawValue === 'number' && Number.isFinite(rawValue) ? rawValue : null;
    return {
      label: String(row.label),
      measuredValue,
      carryForwardValue: measuredValue ?? 0,
    };
  }) ?? null;

const buildMiniMetricLineChartDataFromBuckets = (
  metricBucketsByDate: ReadonlyMap<string, DailyExecutionMetricBucket>,
  dates: string[],
  key: 'passRate' | 'frequency' | 'duration' | 'flakeScore',
): MiniMetricLineChartDatum[] => {
  let carryForwardValue = 0;
  let previousFlakeOutcome: 'passed' | 'notPassed' | null = null;

  return dates.map((label) => {
    const bucket = metricBucketsByDate.get(label);
    const measuredValue =
      bucket === undefined
        ? null
        : key === 'passRate'
          ? bucket.completed + bucket.failed > 0
            ? (bucket.completed / (bucket.completed + bucket.failed)) * 100
            : 0
          : key === 'frequency'
            ? bucket.total
            : key === 'duration'
              ? bucket.durationCount > 0
                ? bucket.durationSumSeconds / bucket.durationCount
                : 0
              : (bucket.flakeScore ??
                (() => {
                  if (bucket.flakeOutcomes.length === 0) return 0;
                  const outcomes =
                    previousFlakeOutcome === null
                      ? bucket.flakeOutcomes
                      : [previousFlakeOutcome, ...bucket.flakeOutcomes];
                  let flips = 0;
                  for (let index = 1; index < outcomes.length; index += 1) {
                    if (outcomes[index] !== outcomes[index - 1]) {
                      flips += 1;
                    }
                  }
                  return outcomes.length > 1 ? (flips / (outcomes.length - 1)) * 100 : 0;
                })());

    if (key === 'flakeScore' && bucket !== undefined && bucket.flakeOutcomes.length > 0) {
      previousFlakeOutcome = bucket.flakeOutcomes[bucket.flakeOutcomes.length - 1] ?? null;
    }

    if (measuredValue !== null) {
      carryForwardValue = measuredValue;
    }

    return {
      label,
      measuredValue,
      carryForwardValue,
    };
  });
};

export const buildMetricsChartModelFromBuckets = <TStatus extends string>({
  statusCountsByDate,
  visibleStatuses,
  statusSeries,
  metricBucketsByDate,
  chartDates,
  metricValueItems,
  showMetricCharts = true,
}: {
  statusCountsByDate: ReadonlyMap<string, Record<TStatus, number>>;
  visibleStatuses: TStatus[];
  statusSeries: Array<{ key: TStatus; label: string; color?: string; colorKey?: UiTone }>;
  metricBucketsByDate: ReadonlyMap<string, DailyExecutionMetricBucket>;
  chartDates: string[];
  metricValueItems: MetricValueItem[];
  showMetricCharts?: boolean;
}): MetricsStatusChartPanelDisplayModel =>
  (() => {
    const metricChartData = showMetricCharts
      ? buildMetricChartSeriesFromBuckets(metricBucketsByDate, chartDates)
      : {
          dates: chartDates,
          passRateChartData: [],
          frequencyChartData: [],
          durationChartData: [],
          flakeScoreChartData: [],
        };

    return buildMetricsChartModelOverride({
      statusChartData: {
        data: buildStatusChartRows(statusCountsByDate, visibleStatuses, chartDates),
        series: statusSeries,
      },
      passRateChartData: metricChartData.passRateChartData,
      frequencyChartData: metricChartData.frequencyChartData,
      durationChartData: metricChartData.durationChartData,
      flakeScoreChartData: metricChartData.flakeScoreChartData,
      passRateMiniChartData: showMetricCharts
        ? buildMiniMetricLineChartDataFromBuckets(
            metricBucketsByDate,
            metricChartData.dates,
            'passRate',
          )
        : [],
      frequencyMiniChartData: showMetricCharts
        ? buildMiniMetricLineChartDataFromBuckets(
            metricBucketsByDate,
            metricChartData.dates,
            'frequency',
          )
        : [],
      durationMiniChartData: showMetricCharts
        ? buildMiniMetricLineChartDataFromBuckets(
            metricBucketsByDate,
            metricChartData.dates,
            'duration',
          )
        : [],
      flakeScoreMiniChartData: showMetricCharts
        ? buildMiniMetricLineChartDataFromBuckets(
            metricBucketsByDate,
            metricChartData.dates,
            'flakeScore',
          )
        : [],
      metricValueItems,
      facetCounts: {
        statusCounts: buildStatusFacetCountsFromBuckets(statusCountsByDate),
        entityCounts: {},
      },
    });
  })();
